import { useEffect, useState, type ChangeEvent, type DragEvent } from 'react';
import { Button, CloseIcon, DocumentsIcon, UploadIcon } from '@repo/atomic-components';
import { Dialog } from '@repo/atomic-components';
import { classNames as cn } from 'primereact/utils';
import { extractFileListFromDataTransfer } from '@/utils/extractFileListFromDataTransfer';

export interface AddMatterFilesModalProps {
  visible: boolean;
  onHide: () => void;
  onSelectFromCase: () => void;
  onUploadFromComputer: () => void;
  onFileDrop?: (event: ChangeEvent<HTMLInputElement>) => void;
  caseRefForBack?: string;
}

export const AddMatterFilesModal = ({
  visible,
  onHide,
  onSelectFromCase,
  onUploadFromComputer,
  onFileDrop,
  caseRefForBack,
}: AddMatterFilesModalProps) => {
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!visible) return;

    const isFileDrag = (e: globalThis.DragEvent) => e.dataTransfer?.types.includes('Files');

    // Set on every drag event — Windows stops firing `dragover` while the cursor is still.
    const handleDragOver = (e: globalThis.DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      setIsDragging(true);
    };

    // Hide only when the cursor leaves the window (null `relatedTarget` or outside the viewport).
    const handleDragLeave = (e: globalThis.DragEvent) => {
      if (!isFileDrag(e)) return;
      const leftWindow =
        e.relatedTarget === null ||
        e.clientX <= 0 ||
        e.clientY <= 0 ||
        e.clientX >= window.innerWidth ||
        e.clientY >= window.innerHeight;
      if (leftWindow) setIsDragging(false);
    };

    const reset = () => setIsDragging(false);

    document.addEventListener('dragenter', handleDragOver);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('dragend', reset);
    document.addEventListener('drop', reset);

    return () => {
      document.removeEventListener('dragenter', handleDragOver);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('dragend', reset);
      document.removeEventListener('drop', reset);
    };
  }, [visible]);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const fileList = extractFileListFromDataTransfer(e.dataTransfer);
    if (fileList?.length && onFileDrop) {
      onFileDrop({ target: { files: fileList } } as ChangeEvent<HTMLInputElement>);
      onHide();
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      modal
      draggable={false}
      closable={false}
      header={
        <div className="text-theme-black flex w-full items-center justify-between text-lg font-semibold">
          <span className="text-[1.25rem] leading-7.5">Add more files</span>
          <Button
            type="button"
            onClick={onHide}
            outlined
            className="text-theme-black hover:bg-theme-background-grey hover:text-theme-black flex h-8 w-8 items-center justify-center rounded border-none transition-colors"
            aria-label="Close"
          >
            <CloseIcon className="h-[16px] w-[16px]" />
          </Button>
        </div>
      }
      className="!w-[min(30rem,calc(100vw-48px))]"
      pt={{
        root: { className: 'rounded-lg dialog-shadow' },
        header: {
          className:
            'min-h-[4.5625rem] cursor-default !rounded-t-lg bg-theme-white text-theme-black border-theme-border-grey border-b px-6 py-4',
        },
        headerTitle: { className: '!m-0 !w-full' },
        content: { className: 'bg-theme-white rounded-b-lg px-6 py-6' },
        closeButton: { className: 'hidden' },
      }}
    >
      <div onDragOver={handleDragOver} onDrop={handleDrop}>
        {isDragging ? (
          <div className="border-theme-dark-green bg-theme-highlight-green flex h-47.5 items-center justify-center rounded-lg border border-dashed">
            <div className="flex flex-col items-center gap-2.5">
              <UploadIcon className="text-theme-dark-green h-[20px] w-[19px]" />
              <span className="text-theme-dark-green text-xs font-bold">Drop to upload files</span>
            </div>
          </div>
        ) : (
          <>
            <p className="text-theme-text-grey mb-6 text-xs leading-5">
              Choose how you&apos;d like to add files to this chat or drag and drop
            </p>
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={onSelectFromCase}
                className={cn(
                  'border-theme-border-grey hover:bg-theme-background-grey flex w-full cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
                )}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                  <DocumentsIcon width={18} height={18} className="text-theme-black" />
                </span>
                <span className="flex flex-col gap-0.5">
                  <span className="text-theme-black text-sm font-semibold">Select from case {caseRefForBack}</span>
                </span>
              </button>
              <button
                type="button"
                onClick={onUploadFromComputer}
                className={cn(
                  'border-theme-border-grey hover:bg-theme-background-grey flex w-full cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
                )}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                  <UploadIcon className="text-theme-black" />
                </span>
                <span className="flex flex-col gap-0.5">
                  <span className="text-theme-black text-sm font-semibold">Upload from computer</span>
                </span>
              </button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};
